import React, { useEffect, useState, useRef } from 'react';
import {
  FormControlLabel,
  Switch,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  FormHelperText,
  Box,
} from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CloseIcon from '@mui/icons-material/Close';
import { Handle, Position } from 'reactflow';
import { useRecoilValue } from 'recoil';
import { botFlowDataAtom, flowObjAtom, flowValidationAtom } from '../../../../Services/_states';
import { FlowBuilderActions } from '../../../../Services/_actions';
import LoadingWrapper from '../../../Shared/LoadingWrapper';
import SearchableDropdown from '../../Common/SearchableDropdown/SearchableDropdown.tsx';
import { useSnackBar } from '../../Common/SnackBar/index.tsx';
import RenderTextArea from './RenderTextArea.tsx';
import FileInput from '../../../Shared/FileUploadPreview/FileInput.jsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../../../Whatsapp/Template/template.constant.js';
import { getAllParentNodeResults } from '../../helper.tsx';

const CtaUrlNode = ({ data }: any) => {
  const actions = FlowBuilderActions();
  const flowValidationObj = useRecoilValue(flowValidationAtom);
  const botFlowData: any = useRecoilValue(botFlowDataAtom);
  const { showSnackBar } = useSnackBar();

  const [isLoading, setIsLoading] = useState(false);
  const [bodyText, setBodyText] = useState(data?.body || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);

  const [ctaUrl, setCtaUrl] = useState(data?.ctaUrl || '');
  const [ctaLabel, setCtaLabel] = useState(data?.ctaLabel || '');
  const flowObj = useRecoilValue(flowObjAtom);

  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (data.body !== bodyText) setBodyText(data.body || '');
    if (data.ctaUrl !== ctaUrl) setCtaUrl(data.ctaUrl || '');
    if (data.ctaLabel !== ctaLabel) setCtaLabel(data.ctaLabel || '');
  }, [data]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12}px`;
    }
  }, [bodyText]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBodyText(value);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const match = textBeforeCursor.match(/{{@\w*$/);
    setShowSuggestions(!!match);
    setCursorPosition(cursorPos);

    // TODO: you can also handle params like ButtonNode if needed

    data.onChange(data.id, { ...data, body: value });
  };

  const handleCtaUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCtaUrl(value);
    data.onChange(data.id, { ...data, ctaUrl: value });
  };

  const handleCtaLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 20);
    setCtaLabel(value);
    data.onChange(data.id, { ...data, ctaLabel: value });
  };

  const inferDocType = (mime: string): 'image' | 'video' | 'audio' | 'file' | null => {
    if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
    if (WHATSAPP_ALLOWED_MIME.video.includes(mime)) return 'video';
    if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
    if (WHATSAPP_ALLOWED_MIME.file.includes(mime)) return 'file';
    return null;
  };
  const bytesToMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const ext = fileName.split('.').pop();

      // Check blocked extensions
      if (BLOCKED_EXTENSIONS.has(ext)) {
        showSnackBar(`"${ext}" files are not allowed for security reasons.`, 'error');
        e.target.value = '';
        return;
      }

      // Infer type group (image, video, audio, document)
      const typeGroup = inferDocType(file.type);
      if (!typeGroup) {
        showSnackBar(`Unsupported file type (${file.type || ext}).`, 'error');
        e.target.value = '';
        return;
      }

      // Check file size
      const limit = WHATSAPP_LIMITS[typeGroup];
      if (file.size > limit) {
        showSnackBar(
          `File too large for ${typeGroup}. Max ${bytesToMB(limit)} MB. Your file is ${bytesToMB(file.size)} MB.`,
          'error'
        );
        e.target.value = '';
        return;
      }

      setIsLoading(true);
      const filename = file.name;
      const formData = new FormData();
      formData.append('bot_number', botFlowData?.phone_number_id || '');
      formData.append('uploaded_file', file);
      formData.append('filename', filename);

      try {
        const resp = await actions.uploadMediaToWhatsapp(formData);
        if (resp?.status === 200) {
          const { messaging_product, ...updatedData } = resp?.data;
          const updatedMedia = {
            ...updatedData,
            filename: filename,
          };
          data.onChange(data.id, { media: updatedMedia });
          showSnackBar(`${type} uploaded successfully!`, 'success');
        } else {
          showSnackBar(resp?.response?.data?.detail || 'Error uploading file', 'error');
        }
      } catch (err) {
        console.error('Error uploading file:', err);
        showSnackBar('Error uploading file', 'error');
      }

      setIsLoading(false);
    }
  };

  // Example: file upload for header media similar to ButtonNode (optional)

  return (
    <div style={{ position: 'relative' }}>
      <Box mb={2}>
        <FormControlLabel
          control={
            <Switch
              checked={data.ai_mode_enabled ?? false}
              onChange={(e) => {
                const enabled = e.target.checked;

                if (!enabled) {
                  data.onChange(data.id, { ai_mode_enabled: false, ai_query_parameters: [] });
                } else {
                  data.onChange(data.id, { ai_mode_enabled: true });
                }
              }}
              color="primary"
            />
          }
          label="AI"
          labelPlacement="end"
        />
        {data.ai_mode_enabled && (
          <SearchableDropdown
            options={parentResults.map((item) => ({
              label: item.label,
              value: item.value,
            }))}
            multiSelect={true}
            onChange={(selectedOptions) => {
              const selectedValues = (selectedOptions as any[]).map((opt) => opt.value);
              data.onChange(data.id, { ai_query_parameters: selectedValues });
            }}
            value={parentResults
              .map((item) => ({ label: item.label, value: item.value }))
              .filter((item) => (data.ai_query_parameters || []).includes(item.value))}
          />
        )}
      </Box>
      <SearchableDropdown
        label="Select Header Type"
        value={data?.headerType}
        options={[
          { label: 'Text', value: 'text' },
          { label: 'Image', value: 'image' },
          { label: 'Document', value: 'document' },
          { label: 'Video', value: 'video' },
        ]}
        onChange={(value) => {
          data.onChange(data.id, { headerType: value || null, media: '' });
        }}
      />

      {/* Header input area (similar to ButtonNode) */}
      {data?.headerType?.value === 'text' && (
        <input
          type="text"
          value={data?.header || ''}
          onChange={(e) => data.onChange(data.id, { header: e.target.value.slice(0, 60) })}
          placeholder="Enter Header Text"
          style={{
            width: '100%',
            padding: '5px',
            marginTop: 5,
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: '#f5f5f5',
          }}
        />
      )}

      {/* Conditional file upload based on the selected type */}
      {['image', 'video', 'document'].includes(data?.headerType?.value) && (
        <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
          {isLoading ? (
            <LoadingWrapper />
          ) : (
            <>
              <label>
                Upload{' '}
                {data?.headerType?.value?.charAt(0)?.toUpperCase() +
                  data?.headerType?.value.slice(1)}
                :
              </label>
              {/* <input
                type="file"
                accept={data?.headerType?.value === 'image' ? 'image/*' : data?.headerType?.value === 'video' ? 'video/*' : '.pdf,.doc,.docx'}
                onChange={(e) => handleFileUpload(e, data?.headerType?.value)}
                style={{ width: '100%', marginBottom: '10px' }}
              /> */}
              <FileInput
                onFileSelect={(e) => handleFileUpload(e, data?.headerType?.value)}
                accept={
                  data?.headerType?.value === 'image'
                    ? 'image/*'
                    : data?.headerType?.value === 'video'
                      ? 'video/*'
                      : '.pdf,.doc,.docx'
                }
              />
              {data?.headerType?.value === 'image' && (
                <FormHelperText sx={{ mt: 0 }}>Allowed: JPG, PNG, GIF • Max 10 MB</FormHelperText>
              )}

              {data?.headerType?.value === 'video' && (
                <FormHelperText sx={{ mt: 0 }}>Allowed: MP4, WebM • Max 16 MB</FormHelperText>
              )}

              {data?.headerType?.value === 'document' && (
                <FormHelperText sx={{ mt: 0 }}>
                  Allowed: PDF, DOC, DOCX, TXT • Max 100 MB
                </FormHelperText>
              )}
            </>
          )}
        </div>
      )}

      {/* Display uploaded content */}
      {data?.media?.s3_object?.url && data?.headerType?.value === 'image' && (
        <div style={{ position: 'relative', marginTop: '15px' }}>
          <CloseIcon
            style={{
              position: 'absolute',
              top: '-52px',
              right: '-6px',
              color: 'red',
              cursor: 'pointer',
              fontSize: '18px',
            }}
            onClick={() => data.onChange(data.id, { media: {} })}
          />
          <img
            src={data?.media?.s3_object?.url}
            alt="Uploaded"
            style={{ width: '100%', borderRadius: '4px' }}
          />
        </div>
      )}

      {data?.media?.s3_object?.url && data?.headerType?.value === 'video' && (
        <div style={{ position: 'relative', marginTop: '15px' }}>
          <CloseIcon
            style={{
              position: 'absolute',
              top: '-52px',
              right: '-6px',
              color: 'red',
              cursor: 'pointer',
              fontSize: '18px',
            }}
            onClick={() => data.onChange(data.id, { media: {} })}
          />
          <video
            controls
            src={data?.media?.s3_object?.url}
            style={{ width: '100%', borderRadius: '4px' }}
          />
        </div>
      )}

      {data?.media?.s3_object?.url && data?.headerType?.value === 'document' && (
        <div style={{ marginTop: '15px' }}>
          <CloseIcon
            style={{
              position: 'absolute',
              top: '112px',
              right: '-2px',
              color: 'red',
              cursor: 'pointer',
              fontSize: '18px',
            }}
            onClick={() => data.onChange(data.id, { media: {} })}
          />
          <a
            href={data?.media?.s3_object?.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block', color: '#007bff', textDecoration: 'underline' }}
          >
            View Document
          </a>
        </div>
      )}

      {/* <label>Body:</label>
      <textarea
        ref={textareaRef}
        value={bodyText}
        onChange={handleBodyChange}
        maxLength={4096}
        placeholder="Enter text"
        style={{
          width: '100%',
          padding: '5px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          background: '#f5f5f5',
          resize: 'none',
          overflow: 'hidden',
        }}
      />
      <div style={{ fontSize: 12, color: bodyText.length === 4096 ? 'red' : '#888', textAlign: 'right' }}>
        {`${bodyText.length}/4096`}
      </div>

      {showSuggestions && (
        <RenderTextArea
          data={data}
          bodyText={bodyText}
          setBodyText={setBodyText}
          cursorPosition={cursorPosition}
          setCursorPosition={setCursorPosition}
          setShowSuggestions={setShowSuggestions}
          parentResults={data?.parentResults} // Pass parentResults here
        />
      )} */}
      <RenderTextArea data={data} />
      {flowValidationObj[data?.id]?.body && (
        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
          {flowValidationObj[data?.id]?.body}
        </Typography>
      )}
      {/* CTA Label and URL inputs */}
      <label>CTA Button Label:</label>
      <input
        type="text"
        value={ctaLabel}
        onChange={handleCtaLabelChange}
        placeholder="Enter CTA Button Label"
        maxLength={20}
        style={{
          width: '100%',
          padding: '5px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          background: '#f5f5f5',
          marginBottom: 10,
        }}
      />
      <div
        style={{ fontSize: 12, color: ctaLabel.length === 20 ? 'red' : '#888', textAlign: 'right' }}
      >
        {`${ctaLabel.length}/20`}
      </div>

      <label>CTA URL:</label>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Enter CTA URL"
        value={ctaUrl}
        onChange={handleCtaUrlChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    setCtaUrl(text);
                    data.onChange(data.id, { ...data, ctaUrl: text });
                  } catch {
                    showSnackBar('Failed to read clipboard', 'error');
                  }
                }}
                edge="end"
                aria-label="paste"
              >
                <ContentPasteIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Footer input */}
      <label>Footer:</label>
      <input
        type="text"
        value={data?.footer || ''}
        onChange={(e) => data.onChange(data.id, { footer: e.target.value.slice(0, 60) })}
        placeholder="Enter Footer Text"
        maxLength={60}
        style={{
          width: '100%',
          padding: '5px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          background: '#f5f5f5',
          marginTop: 5,
        }}
      />
      <div
        style={{
          fontSize: 12,
          color: (data?.footer?.length || 0) === 60 ? 'red' : '#888',
          textAlign: 'right',
        }}
      >
        {`${(data?.footer || '').length}/60`}
      </div>

      {/* Handles */}
      <Handle
        type="source"
        position={Position.Right}
        id={`${data.id}-cta-url`}
        style={{
          background: '#555',
          height: '10px',
          width: '10px',
          borderRadius: '50%',
          position: 'absolute',
          right: '-20px',
          top: '55%',
          transform: 'translateY(-50%)',
        }}
        onClick={() => data.onOutputClick?.(data.id)}
      />
    </div>
  );
};

export default CtaUrlNode;
